<template>
  <gg-modal :is-active="isActive" @close="close">
    <template v-slot:toolbar>
      {{ existing ? 'Edit Automatic Reply' : 'Create Automatic Reply' }}
    </template>
    <template v-slot:body>
      <gg-loading v-if="loading" :loading="true" type="list-item" />
      <v-form v-else class="pt-2" @submit.prevent="save">
        <p class="body-2">
          Messages sent to <strong>{{ mailbox.email }}</strong> are answered with
          this reply. The mailbox still receives them.
        </p>
        <v-row>
          <v-col cols="12" class="py-0">
            <gg-text-field
              outlined
              dense
              label="From"
              placeholder="ex. Jane Doe"
              v-model="model.from"
              v-validate="'required'"
              name="From"
              :error-messages="errors.collect('From')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <gg-text-field
              outlined
              dense
              label="Subject"
              placeholder="ex. Out of office"
              v-model="model.subject"
              v-validate="'required'"
              name="Subject"
              :error-messages="errors.collect('Subject')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-textarea
              outlined
              dense
              rows="5"
              label="Message"
              v-model="model.body"
              v-validate="'required'"
              name="Message"
              :error-messages="errors.collect('Message')"
            />
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <gg-text-field
              outlined
              dense
              type="number"
              min="0"
              label="Reply interval (hours)"
              hint="How long before the same sender gets the reply again."
              persistent-hint
              v-model="model.interval"
              v-validate="'required|integer|min_value:0'"
              name="Interval"
              :error-messages="errors.collect('Interval')"
            />
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <v-checkbox
              v-model="model.isHtml"
              color="green"
              class="mt-1"
              label="Message is HTML"
              hide-details
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary :loading="saving" :disabled="loading" @click="save">
        {{ existing ? 'Save' : 'Create Automatic Reply' }}
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions } from 'vuex'

const blankModel = () => ({
  from: '',
  subject: '',
  body: '',
  interval: '24',
  isHtml: false,
})

/**
 * The mailbox's automatic reply (AM-1106). cPanel keeps one per address and
 * `add_auto_responder` overwrites it, so an existing reply is loaded into the
 * form and saved the same way. It starts now and never stops; an existing
 * reply keeps the schedule it already has. The account's Autoresponders tab
 * still offers the full scheduling form.
 */
export default {
  name: 'AutoReplyModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    mailbox: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      model: blankModel(),
      existing: null,
      loading: false,
      saving: false,
    }
  },
  methods: {
    ...mapActions('emailHub', ['loadAutoResponder', 'saveAutoResponder']),
    ...mapActions(['reportError', 'reportSuccess']),
    close () {
      this.$emit('close')
    },
    async open () {
      this.model = blankModel()
      this.existing = null
      this.$validator.reset()

      this.loading = true
      try {
        this.existing = await this.loadAutoResponder({
          serviceId: this.mailbox.serviceId,
          email: this.mailbox.email,
        })
      } catch (e) {
        // Treated as "none yet"; saving will create one.
        console.warn('AutoReplyModal: unable to read the current automatic reply', e)
      } finally {
        this.loading = false
      }

      if (this.existing) {
        this.model = {
          from: this.existing.from || '',
          subject: this.existing.subject || '',
          body: this.existing.body || '',
          interval: String(this.existing.interval ?? 24),
          isHtml: Number(this.existing.is_html) === 1,
        }
      }
    },
    async save () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.saving = true
      try {
        await this.saveAutoResponder({
          serviceId: this.mailbox.serviceId,
          user: this.mailbox.user,
          domain: this.mailbox.domain,
          from: this.model.from,
          subject: this.model.subject,
          body: this.model.body,
          interval: Number(this.model.interval),
          isHtml: this.model.isHtml,
          start: this.existing ? this.existing.start : '',
          stop: this.existing ? this.existing.stop : '',
        })
      } catch (e) {
        this.saving = false
        this.reportError(e)
        return
      }

      this.saving = false
      this.reportSuccess(this.existing ? 'Automatic reply updated.' : 'Automatic reply created.')
      this.close()
    },
  },
  watch: {
    isActive (active) {
      if (active) this.open()
    },
  },
  // Mounted already open from the dashboard row — see AddAliasModal.open().
  created () {
    if (this.isActive) this.open()
  },
}
</script>
